/**
 * The coach account value — one place that answers "where is this coach in the
 * paid lifecycle?", so every screen and every server gate can read the same
 * answer instead of re-deriving billing state.
 *
 * Three concerns are kept apart on purpose:
 *  - lifecycle (here): pre-trial, trial, paid, churned.
 *  - tier (here): which NOVA level the coach holds, mapped from the plan key
 *    stored on the coach row (`coaches.nova_plan_key`).
 *  - features: what a given lifecycle/tier opens. Not modelled here.
 *
 * Pure, with a single type-only import of the plan catalogue — itself a pure
 * shared constant module — so this stays safe to import from any route,
 * component or server module without pulling server-only code into the client
 * bundle.
 *
 * Aliases: coach account state, coach lifecycle, account state
 */

import type { PlanKey } from "~/lib/shared/plan-catalogue";

/**
 * Nova entitlement, collapsed to the access states. Mirrors
 * `NovaAccessState` (`~/lib/server/nova/access`) but is declared locally so
 * this module keeps zero imports. `buildCoachAccount` assigns the real
 * `NovaAccess.state` into this field, so a new access state added upstream
 * fails to compile there rather than drifting silently.
 *
 * `free_access` is NOVA Core given free by Dream Coach Match staff.
 */
export type CoachAccessState =
  | "subscribed"
  | "trialing"
  | "past_due"
  | "free_access"
  | "none";

/**
 * Where the coach sits in the paid lifecycle.
 *
 * `paid` covers `past_due` as well: Stripe is still retrying the card and the
 * coach keeps access for the whole retry window, so the screens treat them as
 * paying. The `pastDue` flag beside the lifecycle is what a payment banner
 * reads.
 *
 * `churned` means access has lapsed after the coach had it — a cancelled
 * subscriber, and also a trial cancelled before it converted.
 *
 * Free access from staff is `paid` on NOVA Core while it lasts. The trial and
 * payment markers come from Stripe alone, so once it ends she is `pre_trial`
 * or `churned` exactly as she would have been without it.
 */
export type CoachLifecycle = "pre_trial" | "trial" | "paid" | "churned";

/** Which NOVA level a coach on paid terms holds. */
export type CoachTier = "core" | "pro" | "pro_plus";

/** Each tier by the name its product carries in Stripe. */
export const COACH_TIER_LABELS: Record<CoachTier, string> = {
  core: "NOVA Core",
  pro: "NOVA Pro",
  pro_plus: "NOVA Pro+",
};

/**
 * The tier each plan grants.
 *
 * `marketing_suite` is the Marketing Suite on its own — no Nova at all — so it
 * is not a Nova tier. It cannot in practice reach here either: a subscription
 * only becomes the coach's tracked Nova subscription when its product is one of
 * the Nova-granting kinds (`isNovaProduct`), which the standalone Suite product
 * is not. Mapped explicitly anyway, so the record stays exhaustive over
 * `PlanKey` and a plan added to the catalogue fails to compile until its tier
 * is decided.
 */
const TIER_BY_PLAN_KEY: Record<PlanKey, CoachTier | null> = {
  nova: "core",
  nova_pro: "pro",
  marketing_suite: null,
  nova_pro_plus: "pro_plus",
};

/** The NOVA tier a plan key grants, or null for none or an unknown plan. */
export function tierForPlanKey(planKey: PlanKey | null): CoachTier | null {
  return planKey ? TIER_BY_PLAN_KEY[planKey] : null;
}

/** Everything the lifecycle is derived from. All plain, serializable values. */
export interface CoachAccountInput {
  /** The coach's Nova access state — pass `NovaAccess.state` as-is. */
  accessState: CoachAccessState;
  /** Whether a Nova trial has ever started (`novaTrialStartedAt` is set). */
  everTrialed: boolean;
  /** Whether a paid Nova subscription has ever started (`novaPaidStartedAt`). */
  everPaid: boolean;
  /**
   * Whether the coach holds the Marketing Suite — pass
   * `holdsMarketingSuite(coach)` (`~/lib/shared/membership-plan`). Passed in
   * rather than derived here so this module never touches a coach row.
   * "Suite only" is `pre_trial` + `hasSuite`.
   */
  hasSuite: boolean;
  /**
   * The plan the coach's tracked Nova subscription bills
   * (`coaches.novaPlanKey`), or null when none is tracked or the plan could not
   * be resolved. Null for every subscriber whose last Stripe event predates the
   * column, until the backfill or their next event fills it in — so a null tier
   * on a paying coach means "unknown", never "no plan".
   */
  planKey: PlanKey | null;
  /**
   * Whether the stored Stripe status is `unpaid` — pass
   * `readNovaSubscription(coach).status === "unpaid"`. Carried separately
   * because the access derivation collapses `unpaid` to `none`, which makes the
   * distinction unrecoverable from the lifecycle alone.
   */
  suspended: boolean;
  /**
   * Whether she has already taken her one win-back trial — pass
   * `coach.novaWinbackTrialStartedAt != null`.
   */
  winbackTrialUsed: boolean;
  /**
   * Whether Stripe still holds a Nova subscription for her that has not ended:
   * any stored status except `canceled` and `incomplete_expired`. A checkout
   * would put a second subscription beside it.
   */
  holdsSubscription: boolean;
  /** Whether she came in through the program lane, which never gets a free trial. */
  programLane: boolean;
  /** Her Business Builder Accelerator standing, read from the coach row. */
  accelerator: CoachAccelerator;
}

/**
 * Where the coach stands with the Business Builder Accelerator. Independent of
 * the lifecycle: she keeps the Accelerator when NOVA lapses, and it does not
 * depend on her NOVA plan.
 */
export interface CoachAccelerator {
  /** She bought it through Stripe, and an admin has not cleared it after a refund. */
  purchased: boolean;
  /** An admin or the graduate import recorded her graduation. */
  graduate: boolean;
  /** Her cohort, once an admin or the import set one. Every graduate has one. */
  cohort: number | null;
}

/** The account value every coach screen and server gate reads. */
export interface CoachAccount {
  lifecycle: CoachLifecycle;
  /**
   * The subscription is in Stripe's retry window. Always false unless the
   * lifecycle is `paid` — access is unaffected; only the banner reads this.
   */
  pastDue: boolean;
  /**
   * She holds the Marketing Suite, so the Marketing Suite menu item opens it
   * instead of its sales page. Independent of the lifecycle: the Suite on its
   * own gives no NOVA, so a Suite-only coach is `pre_trial` with this set.
   */
  hasSuite: boolean;
  /**
   * The NOVA level the coach holds, and only while they hold it: non-null for
   * `trial` and `paid`, null for `pre_trial` and `churned`. A churned coach's
   * plan key is cleared with the rest of their subscription columns, so "what
   * they used to be on" is deliberately not answerable from here.
   *
   * Null on a `paid` or `trial` coach means the plan key is not resolved yet —
   * see {@link CoachAccountInput.planKey}. Treat it as unknown, not as "core".
   */
  tier: CoachTier | null;
  /**
   * Stripe still holds the subscription with its retries spent and an invoice
   * outstanding (`unpaid`). Access is denied, so the lifecycle is `churned` —
   * but a suspended coach is not a lost one: route her to the billing portal to
   * settle the outstanding invoice and reactivate what she already has, never
   * to a win-back that sells her a second subscription.
   */
  suspended: boolean;
  /**
   * She can take her one free 7-day win-back trial. Always false unless the
   * lifecycle is `churned` — see {@link isWinbackTrialEligible}. The checkout
   * decides the trial for itself; screens read this only to say so.
   */
  winbackTrialEligible: boolean;
  /** Her Business Builder Accelerator standing, whatever the lifecycle. */
  accelerator: CoachAccelerator;
}

/**
 * She holds the Marketing Suite and no NOVA trial or paid plan: pre-trial, or
 * her plan has ended (a suspended plan included). The Marketing Suite menu item
 * opens its "Your plan" page for her. A coach with a NOVA trial or paid plan,
 * Core included, opens the Suite itself.
 */
export function holdsSuiteOnly(
  account: Pick<CoachAccount, "hasSuite" | "lifecycle">,
): boolean {
  return (
    account.hasSuite &&
    (account.lifecycle === "pre_trial" || account.lifecycle === "churned")
  );
}

/** The plan pill in the app top bar: a status label, never an offer. */
export interface CoachPlanLabel {
  label: string;
  /** `warning` while money is owed; the pill still only reports it. */
  tone: "neutral" | "warning";
}

/**
 * The plan pill's words for each account state, or null when there is no plan
 * to report: a pre-trial coach has not started NOVA, and "pre-trial" is our
 * word, not hers. A trial or paid coach whose tier is not resolved yet gets a
 * generic label rather than a guessed tier.
 * A suspended coach is `churned`, but her plan has not ended: an unpaid invoice
 * stands between her and it, so she reads "Payment issue" like a coach whose
 * card Stripe is still retrying.
 */
export function coachPlanLabel(
  account: Pick<CoachAccount, "lifecycle" | "tier" | "pastDue" | "suspended">,
): CoachPlanLabel | null {
  const plan = account.tier ? COACH_TIER_LABELS[account.tier] : null;
  switch (account.lifecycle) {
    case "pre_trial":
      return null;
    case "trial":
      return { label: plan ? `${plan} trial` : "Free trial", tone: "neutral" };
    case "paid":
      return account.pastDue
        ? { label: "Payment issue", tone: "warning" }
        : { label: plan ?? "NOVA plan", tone: "neutral" };
    case "churned":
      return account.suspended
        ? { label: "Payment issue", tone: "warning" }
        : { label: "Plan ended", tone: "neutral" };
    default: {
      const exhaustive: never = account.lifecycle;
      throw new Error(`Unknown coach lifecycle: ${String(exhaustive)}`);
    }
  }
}

/** Everything the win-back trial rule reads. */
export type WinbackTrialInput = Pick<
  CoachAccountInput,
  "winbackTrialUsed" | "holdsSubscription" | "programLane"
> & { lifecycle: CoachLifecycle };

/**
 * Whether a coach can take the free 7-day win-back trial: her access lapsed,
 * she has never taken a win-back trial, and Stripe holds no Nova subscription
 * for her that could still charge. A coach who trialled and never paid
 * qualifies as much as one who paid for months.
 *
 * The program lane is excluded for the reason it never gets the first trial:
 * those coaches are sold to directly, never offered a free week.
 *
 * One per coach, for life: a second lapse after a win-back trial has only the
 * paid way back.
 */
export function isWinbackTrialEligible(input: WinbackTrialInput): boolean {
  return (
    input.lifecycle === "churned" &&
    !input.winbackTrialUsed &&
    !input.holdsSubscription &&
    !input.programLane
  );
}

function deriveLifecycle(input: CoachAccountInput): CoachLifecycle {
  switch (input.accessState) {
    case "trialing":
      return "trial";
    case "subscribed":
    case "past_due":
    case "free_access":
      return "paid";
    case "none":
      // Either marker is enough: a coach can reach paid without ever trialing
      // (bundle and payment-link buyers get no trial), and a trial cancelled
      // before it converts ends without ever paying. Both are write-once, so
      // a lapsed coach never reads as new again.
      return input.everTrialed || input.everPaid ? "churned" : "pre_trial";
    default: {
      const exhaustive: never = input.accessState;
      throw new Error(`Unknown coach access state: ${String(exhaustive)}`);
    }
  }
}

/** Derives the account value. Pure — no I/O, total over its inputs. */
export function deriveCoachAccount(input: CoachAccountInput): CoachAccount {
  const lifecycle = deriveLifecycle(input);
  // Gated on the lifecycle rather than reported whenever a plan key survives:
  // the tier answers "what does this coach have", and a coach with no access
  // has no tier. The column is cleared on cancellation anyway, so this is a
  // guarantee rather than a filter.
  const entitled = lifecycle === "trial" || lifecycle === "paid";
  // Free access is NOVA Core whatever plan key a past subscription left.
  const tier =
    input.accessState === "free_access"
      ? "core"
      : entitled
        ? tierForPlanKey(input.planKey)
        : null;
  return {
    lifecycle,
    pastDue: input.accessState === "past_due",
    hasSuite: input.hasSuite,
    tier,
    suspended: input.suspended,
    winbackTrialEligible: isWinbackTrialEligible({ ...input, lifecycle }),
    accelerator: input.accelerator,
  };
}
